import { useEffect, useRef, useState } from 'react';
import { MessageCircle, Loader2, Send, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { useAlertModal } from '../AlertModalProvider';

interface ContatoSugestao {
  id: number;
  nome: string;
  push_name: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
}

interface Props {
  titulo?: string;
  // Cliente do cadastro pra pre-preencher (usa whatsapp ou telefone do cadastro). Opcional.
  clienteId?: number;
  // Telefone direto (alternativa ao clienteId, ex: fornecedor de cotacao)
  telefoneInicial?: string;
  // Nome exibido no topo do modal pra contexto
  contextoNome?: string;
  // Contexto extra exibido em <small> (ex: "Cotacao 0014")
  contextoExtra?: string;
  // Quando o usuario confirma envio: recebe digits-only e executa o POST.
  // Throw pra abortar (modal mostra erro).
  onEnviar: (digits: string) => Promise<void>;
  onClose: () => void;
  // Filtra busca de contatos por inbox. 'atendimento' = clientes; 'compras' = fornecedores.
  // Omitir traz todos.
  inboxFiltro?: 'atendimento' | 'compras';
}

function formatarTelefoneBr(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 13);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `+${d.slice(0, 2)} ${d.slice(2)}`;
  if (d.length <= 9) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4)}`;
  if (d.length <= 12) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 8)}-${d.slice(8)}`;
  return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9)}`;
}

function pareceTelefone(v: string): boolean {
  if (!v) return false;
  const apenasFone = /^[\d\s+\-()]+$/.test(v);
  const digitos = v.replace(/\D/g, '');
  return apenasFone && digitos.length >= 3;
}

export function ModalEnviarWhatsApp({
  titulo = 'Enviar pelo WhatsApp',
  clienteId,
  telefoneInicial,
  contextoNome,
  contextoExtra,
  onEnviar,
  onClose,
  inboxFiltro,
}: Props) {
  const toast = useToast();
  const { alerta } = useAlertModal();
  const [texto, setTexto] = useState('');
  const [whatsappFinal, setWhatsappFinal] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [sugestoes, setSugestoes] = useState<ContatoSugestao[]>([]);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pre-preenche com telefoneInicial OR whatsapp do cliente
  useEffect(() => {
    if (telefoneInicial) {
      const digits = telefoneInicial.replace(/\D/g, '');
      if (digits) {
        setTexto(formatarTelefoneBr(digits));
        setWhatsappFinal(digits);
      }
      return;
    }
    if (!clienteId || clienteId === 1) return;
    let cancelado = false;
    api.get<{ whatsapp?: string | null; telefone?: string | null }>(`/api/clientes/${clienteId}`)
      .then((c) => {
        if (cancelado) return;
        const fone = c.whatsapp || c.telefone || '';
        const digits = fone.replace(/\D/g, '');
        if (digits) {
          setTexto(formatarTelefoneBr(digits));
          setWhatsappFinal(digits);
        }
      })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [clienteId, telefoneInicial]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!texto || texto.length < 2 || pareceTelefone(texto)) {
      setSugestoes([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const inboxParam = inboxFiltro ? `&inbox=${inboxFiltro}` : '';
        const lista = await api.get<ContatoSugestao[]>(`/api/contatos?busca=${encodeURIComponent(texto)}&sem_grupo=true${inboxParam}&limit=20`);
        // Dedup por telefone (mesmo numero pode ter 2 contatos: 1 por inbox atendimento/compras).
        const porDigits = new Map<string, ContatoSugestao>();
        for (const c of lista) {
          if (!c.telefone) continue;
          const digits = c.telefone.replace(/\D/g, '');
          if (!digits) continue;
          const existente = porDigits.get(digits);
          if (!existente) { porDigits.set(digits, c); continue; }
          const novoMelhor = (!!c.foto_url && !existente.foto_url) || (!!c.push_name && !existente.push_name);
          if (novoMelhor) porDigits.set(digits, c);
        }
        setSugestoes([...porDigits.values()]);
        setMostrarSugestoes(true);
      } catch {
        setSugestoes([]);
      } finally {
        setBuscando(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [texto]);

  function escolherContato(c: ContatoSugestao) {
    const digitos = (c.telefone ?? '').replace(/\D/g, '');
    setTexto(`${c.nome || c.push_name || ''} (${formatarTelefoneBr(digitos)})`);
    setWhatsappFinal(digitos);
    setMostrarSugestoes(false);
  }

  function onChangeTexto(v: string) {
    setTexto(v);
    setMostrarSugestoes(true);
    if (pareceTelefone(v)) setWhatsappFinal(v.replace(/\D/g, ''));
    else setWhatsappFinal('');
  }

  async function enviar() {
    const digits = whatsappFinal || texto.replace(/\D/g, '');
    if (digits.length < 10) {
      alerta('Selecione um contato ou informe um numero valido (com DDD).', { titulo: 'WhatsApp invalido', tipo: 'aviso' });
      return;
    }
    setEnviando(true);
    try {
      await onEnviar(digits);
      toast.sucesso('Enviado pelo WhatsApp');
      onClose();
    } catch (err: any) {
      alerta(err?.message || 'Falha ao enviar pelo WhatsApp', { titulo: 'Erro no envio', tipo: 'erro' });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <MessageCircle size={20} className="text-green-600" />
            {titulo}
          </h3>
          <button type="button" className="btn-fechar-modal" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="p-5 space-y-3">
          {(contextoNome || contextoExtra) && (
            <div className="text-sm text-gray-600">
              {contextoNome && <div>Para: <strong>{contextoNome}</strong></div>}
              {contextoExtra && <div className="text-xs text-gray-500">{contextoExtra}</div>}
            </div>
          )}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
              Buscar contato ou digitar numero
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={texto}
                onChange={(e) => onChangeTexto(e.target.value)}
                onFocus={() => setMostrarSugestoes(true)}
                placeholder="Digite nome ou +55 64 9XXXX-XXXX"
                autoFocus
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-green-500"
              />
              {buscando && (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
              )}
            </div>

            {mostrarSugestoes && sugestoes.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto z-10">
                {sugestoes.map((c) => {
                  const nome = c.nome || c.push_name || c.telefone || '?';
                  const fone = c.telefone ? formatarTelefoneBr(c.telefone) : '';
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => escolherContato(c)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-green-50 text-left border-b border-gray-100 last:border-b-0"
                    >
                      {c.foto_url ? (
                        <img src={c.foto_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600 flex-shrink-0">
                          {nome.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-gray-800 truncate">{nome}</div>
                        <div className="text-sm text-gray-500">{fone}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={enviando}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => void enviar()}
            disabled={enviando}
            className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-60 inline-flex items-center gap-2"
          >
            {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {enviando ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}
